import type { CmmdTaskStatus, EnvironmentId, ProjectId } from "@cmmd-forge/contracts";

import type { ManualCommandTask } from "./NoActiveThreadState.logic";

export const COMMAND_CENTER_VIEW_KEY = "forge:command-center:view";
export const COMMAND_CENTER_STATUS_FILTER_KEY = "forge:command-center:status-filter";
export const COMMAND_CENTER_TEAM_FILTER_KEY = "forge:command-center:team-filter";
export const COMMAND_CENTER_CAMPAIGN_FILTER_KEY = "forge:command-center:campaign-filter";
export const COMMAND_CENTER_PROJECT_FILTER_KEY = "forge:command-center:project-filter";
export const COMMAND_CENTER_ASSIGNEE_FILTER_KEY = "forge:command-center:assignee-filter";
export const COMMAND_CENTER_SEARCH_QUERY_KEY = "forge:command-center:search";
export const COMMAND_CENTER_MANUAL_TASKS_KEY = "forge:command-center:manual-tasks:v1";

function getLocalStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  return window.localStorage;
}

export function readStoredView(): "control" | "board" {
  const value = getLocalStorage()?.getItem(COMMAND_CENTER_VIEW_KEY);
  return value === "control" || value === "board" ? value : "control";
}

export function readStoredStatusFilter(): CmmdTaskStatus | "all" {
  const value = getLocalStorage()?.getItem(COMMAND_CENTER_STATUS_FILTER_KEY);
  return isStoredTaskStatus(value) ? value : "all";
}

export function readStoredTeamFilter(): string {
  return getLocalStorage()?.getItem(COMMAND_CENTER_TEAM_FILTER_KEY) ?? "all";
}

export function readStoredCampaignFilter(): string {
  return getLocalStorage()?.getItem(COMMAND_CENTER_CAMPAIGN_FILTER_KEY) ?? "all";
}

export function readStoredProjectFilter(): string {
  return getLocalStorage()?.getItem(COMMAND_CENTER_PROJECT_FILTER_KEY) ?? "all";
}

export function readStoredAssigneeFilter(): string {
  return getLocalStorage()?.getItem(COMMAND_CENTER_ASSIGNEE_FILTER_KEY) ?? "all";
}

export function readStoredSearchQuery(): string {
  return getLocalStorage()?.getItem(COMMAND_CENTER_SEARCH_QUERY_KEY) ?? "";
}

export function isStoredTaskStatus(value: unknown): value is CmmdTaskStatus {
  return (
    value === "backlog" ||
    value === "todo" ||
    value === "in_progress" ||
    value === "in_review" ||
    value === "blocked" ||
    value === "done" ||
    value === "cancelled"
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseStoredManualTask(item: unknown): ManualCommandTask | null {
  if (!isRecord(item)) return null;
  if (
    typeof item.id !== "string" ||
    !item.id.startsWith("manual:") ||
    typeof item.title !== "string" ||
    item.title.trim().length === 0 ||
    !isStoredTaskStatus(item.status) ||
    typeof item.createdAt !== "string"
  ) {
    return null;
  }

  return {
    id: item.id,
    title: item.title,
    status: item.status,
    projectEnvironmentId:
      typeof item.projectEnvironmentId === "string"
        ? (item.projectEnvironmentId as EnvironmentId)
        : null,
    projectId: typeof item.projectId === "string" ? (item.projectId as ProjectId) : null,
    projectName: typeof item.projectName === "string" ? item.projectName : null,
    cmmdProjectId: typeof item.cmmdProjectId === "string" ? item.cmmdProjectId : null,
    createdAt: item.createdAt,
    updatedAt: typeof item.updatedAt === "string" ? item.updatedAt : item.createdAt,
  };
}

export function readStoredManualTasks(): readonly ManualCommandTask[] {
  const raw = getLocalStorage()?.getItem(COMMAND_CENTER_MANUAL_TASKS_KEY);
  if (!raw) return [];

  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item): ManualCommandTask[] => {
      const task = parseStoredManualTask(item);
      return task ? [task] : [];
    });
  } catch {
    return [];
  }
}

export function writeStoredManualTasks(tasks: readonly ManualCommandTask[]): void {
  getLocalStorage()?.setItem(COMMAND_CENTER_MANUAL_TASKS_KEY, JSON.stringify(tasks));
}
